import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useCompareQuizWebMutation,
  useUpdateQuizVerWebMutation,
} from '../../features/customerApiSlice';
import { selectCurrentLanguage } from '../../features/langSlice';
import { useGetAssetsQuery } from '../../features/quizApiSlice';
import { normalizeLanguageCode } from '../../utils/language';
import ComparisonFormulaCard from './components/ComparisonFormulaCard';
import MobileFormulaComparison from './components/MobileFormulaComparison';
import EditFormulaModal from './components/EditFormulaModal';
import AddIngredientModal from './components/AddIngredientModal';
import useComparisonFormulaDraft from '../../hooks/useComparisonFormulaDraft';
import FormulaUpdateConfirmation from './components/FormulaUpdateConfirmation';

const formatAmount = (amount, currency = 'eur') => {
  if (amount === null || amount === undefined) return '-';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '-';

  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: String(currency || 'eur').toUpperCase(),
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (date) => {
  if (!date) return null;
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return null;

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

function QuizComparison() {
  const selectedLang = useSelector(selectCurrentLanguage);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [compareQuizWeb, { isLoading: isLoadingComparison }] = useCompareQuizWebMutation();
  const [updateQuizVerWeb, { isLoading: isUpdating }] = useUpdateQuizVerWebMutation();
  const [comparison, setComparison] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [isConfirmingRetake, setIsConfirmingRetake] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingIngredients, setIsAddingIngredients] = useState(false);

  const lang = useMemo(
    () => normalizeLanguageCode(selectedLang || localStorage.getItem('lang') || 'en'),
    [selectedLang],
  );
  const { data: focusAreaIcons } = useGetAssetsQuery({ section: 'focusAreaIcon', lang });
  const draft = useComparisonFormulaDraft({ comparison, lang });

  useEffect(() => {
    if (!lang) return;

    compareQuizWeb({ lang })
      .unwrap()
      .then(setComparison)
      .catch(() => setComparison(null));
  }, [compareQuizWeb, lang]);

  const pricing = comparison?.pricing || {};
  const cadenceLabel = pricing.cadenceMonths === 3
    ? t('EveryThreeMonths')
    : pricing.cadenceMonths === 1 ? `/${t('Month')}` : '';
  const currentPrice = `${formatAmount(pricing.currentPrice ?? pricing.roundedCurrentPrice, pricing.currency)} ${cadenceLabel}`.trim();
  const newPrice = `${formatAmount(draft.pricing?.newAmount, pricing.currency)} ${cadenceLabel}`.trim();
  const effectiveDate = formatDate(pricing.effectiveAt);
  const isUpdateBlocked = isUpdating || pricing.scheduleBlocked || draft.isInvalid || draft.isLoadingCatalog;

  const handleSelectVersion = async (version) => {
    if (version === 'current') {
      draft.clearDraft();
      navigate('/dashboard/comparison/message');
      return;
    }
    if (isUpdateBlocked) return;
    setSelectedVersion(version);
    try {
      const result = await updateQuizVerWeb({
        ver: version === 'new' ? 'new' : '',
        new_quiz_id: comparison?.new_quiz_id,
        quiz_id: comparison?.quiz_id,
        ...(draft.hasFormulaChanged ? { ingredientDraft: draft.ingredientDraft } : {}),
      }).unwrap();

      if (result?.scheduled) {
        draft.clearDraft();
        navigate('/dashboard/comparison/message');
        return;
      }

      toast.error(result?.message || t('QuizComparison_UpdateError'));
    } catch (error) {
      toast.error(error?.data?.message || t('QuizComparison_UpdateError'));
    } finally {
      setSelectedVersion('');
    }
  };

  const handleRetakeSelect = () => {
    if (isUpdateBlocked) return;
    setIsConfirmingRetake(true);
  };

  const handleConfirmRetake = async () => {
    setIsConfirmingRetake(false);
    await handleSelectVersion('new');
  };

  return (
    <Fragment>
      <main>
        <section className="t-py-8 md:t-py-10">
          <div className="container">
            <div className="t-w-full">
              <div className="t-mb-7">
                <div>
                  <h1 className="t-font-serif t-text-2xl md:t-text-3xl t-leading-tight t-text-navy t-m-0">
                    {t('QuizComparison_Title')}
                  </h1>
                  <p className="t-m-0 t-mt-2 t-max-w-2xl t-text-sm t-leading-relaxed t-text-teal-muted">
                    {t('QuizComparison_UnchangedNotice')}
                  </p>
                </div>
              </div>

              {isLoadingComparison && (
                <div className="t-rounded-lg t-border t-border-g200 t-bg-white t-p-8 t-text-center t-text-g500">
                  {t('QuizComparison_Loading')}
                </div>
              )}

              {!isLoadingComparison && !comparison && (
                <div className="t-rounded-lg t-border t-border-g200 t-bg-white t-p-8 t-text-center">
                  <h2 className="t-font-serif t-text-2xl t-text-navy t-m-0 t-mb-2">
                    {t('QuizComparison_EmptyTitle')}
                  </h2>
                  <p className="t-text-sm t-text-g500 t-m-0">
                    {t('QuizComparison_EmptyBody')}
                  </p>
                </div>
              )}

              {comparison && pricing.scheduleBlocked && (
                <div className="t-mb-5 t-rounded-lg t-border t-border-[#F3C164] t-bg-[#FFF8E6] t-p-4 t-text-sm t-text-navy">
                  {pricing.reason || t('QuizComparison_BlockedFallback')}
                </div>
              )}

              {comparison && (
                <MobileFormulaComparison comparison={comparison} ingredients={draft.data}
                  focusAreaIcons={focusAreaIcons} currentPrice={currentPrice} newPrice={newPrice} t={t}
                  onKeepCurrent={() => handleSelectVersion('current')} onUseNew={handleRetakeSelect}
                  onEdit={() => setIsEditing(true)} isUpdating={isUpdating} isUpdateBlocked={isUpdateBlocked} />
              )}
              {comparison && (
                <div className="t-hidden t-gap-10 lg:t-grid lg:t-grid-cols-2">
                  <ComparisonFormulaCard
                    title={t('QuizComparison_CurrentTitle')}
                    badge={t('QuizComparison_CurrentBadge')}
                    ingredients={comparison.ingredients || []}
                    allIngredients={draft.allIngredients}
                    focusArea={comparison.focusArea || []}
                    focusAreaIcons={focusAreaIcons}
                    t={t}
                    price={currentPrice}
                    priceLabel={t('QuizComparison_CurrentPrice')}
                    isLoading={isUpdating && selectedVersion === 'current'}
                    onSelect={() => handleSelectVersion('current')}
                    selectLabel={t('QuizComparison_KeepCurrent')}
                    isDisabled={isUpdating}
                  />
                  <ComparisonFormulaCard
                    title={t('QuizComparison_NewTitle')}
                    badge={t('QuizComparison_NewBadge')}
                    ingredients={draft.data}
                    allIngredients={draft.allIngredients}
                    focusArea={comparison.focusArea2 || []}
                    focusAreaIcons={focusAreaIcons}
                    t={t}
                    price={newPrice}
                    priceLabel={t('QuizComparison_NextRenewal')}
                    isLoading={isUpdating && selectedVersion === 'new'}
                    onSelect={handleRetakeSelect}
                    selectLabel={t('QuizComparison_UseNew')}
                    onEdit={() => setIsEditing(true)}
                    isDisabled={isUpdateBlocked}
                  />
                </div>
              )}
              {comparison && <p className="!t-m-0 t-mt-5 t-text-sm t-text-teal-muted">
                {effectiveDate && t('QuizComparison_EffectiveDate', { date: effectiveDate })}
              </p>}
              {comparison && draft.isInvalid && <p role="alert" className="!t-m-0 t-mt-3 t-text-sm t-text-red-600">
                {t('QuizComparison_DraftError')}
              </p>}
            </div>
          </div>
        </section>

        {isConfirmingRetake && (
          <FormulaUpdateConfirmation
            pricing={pricing} currentPrice={currentPrice} newPrice={newPrice}
            effectiveDate={effectiveDate} formatAmount={formatAmount}
            isUpdating={isUpdating} isDisabled={isUpdateBlocked}
            handleClose={() => setIsConfirmingRetake(false)}
            handleConfirm={handleConfirmRetake}
          />
        )}
        <EditFormulaModal
          open={isEditing && !isAddingIngredients}
          handleClose={() => setIsEditing(false)}
          data={draft.data} ingredients={comparison?.ingredients2 || []}
          hasFormulaChanged={draft.hasFormulaChanged}
          setAddIngredientsModal={setIsAddingIngredients}
          allIngredients={draft.allIngredients}
          onRemoveQuantity={draft.onRemoveQuantity} onAddQuantity={draft.onAddQuantity}
          prices={draft.prices} removeIngredientFromBin={draft.removeIngredientFromBin}
          onInputChange={draft.onInputChange} onReset={draft.handleReset}
          name={t('QuizComparison_NewTitle')}
        />
        <AddIngredientModal open={isAddingIngredients} handleClose={() => setIsAddingIngredients(false)}
          allIngredients={draft.allIngredients} data={draft.data}
          removeIngredient={draft.removeIngredient} addIngredient={draft.addIngredient} />
      </main>
    </Fragment>
  );
}

export default QuizComparison;
